/**
 * PuzzleRoyaleTournamentResults (P7)
 *
 * Durable, id-keyed history view for a COMPLETED Puzzle Royale tournament.
 * Rendered by PuzzleRoyaleTournamentDetail (via its `renderResults` prop) once
 * state === 'completed'. Unlike RoyaleResultModal — which reads the live
 * in-race royale store (PlayerRoyaleState) and only exists while the tab that
 * played is open — this reads the REST results joined via `session_id`
 * (RoyaleTournamentDetail.results.players), so it works for anyone opening the
 * tournament from discovery / a deep link long after the race ended.
 *
 * Shows:
 *   - Winner callout + final leaderboard (rank / name / points / solved /
 *     accuracy), winner first.
 *   - "Your race": the viewer's own row expanded to solved / failed puzzle
 *     counts (solved_ids / failed_ids), so a player can review what they got.
 *
 * Visual language mirrors RoyaleResultModal (gold winner, teal accents, stat
 * rows) so completed tournaments feel like the end-of-race screen.
 */

import React, {useMemo, useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';

import useLoggedInUserStore from '@/Store/loggedInUserstore';
import type {
  RoyaleTournamentDetail,
  RoyaleTournamentPlayerResult,
} from '@/Hooks/FunChallengesApi/useRoyaleTournaments';

const TEAL = '#3DAB9E';
const TEAL_DARK = '#1F6C63';
const TEAL_TINT_SOFT = 'rgba(61, 171, 158, 0.06)';
const BROWN = '#4D3F37';
const TEXT_DARK = '#262322';
const BORDER = 'rgba(38, 35, 34, 0.1)';
const GOLD = '#E7B325';
const RED = '#EB5757';

interface Props {
  detail: RoyaleTournamentDetail;
}

/** Roster user_id → display name, so the results table can show names even
 *  though the per-player result row only carries user_id. */
function rosterNameMap(detail: RoyaleTournamentDetail): Record<string, string> {
  const map: Record<string, string> = {};
  (detail.roster || []).forEach(r => {
    if (r.user_name) map[String(r.user_id)] = r.user_name;
  });
  return map;
}

function Medal({rank}: {rank: number}) {
  if (rank === 1) return <CCText style={styles.medal}>🥇</CCText>;
  if (rank === 2) return <CCText style={styles.medal}>🥈</CCText>;
  if (rank === 3) return <CCText style={styles.medal}>🥉</CCText>;
  return (
    <View style={styles.rankNumWrap}>
      <CCText fontSize={FontSizes[12]} color={BROWN} fontWeight="bold">
        {rank}
      </CCText>
    </View>
  );
}

export default function PuzzleRoyaleTournamentResults({detail}: Props) {
  const myUserId = useLoggedInUserStore(
    s => (s.userData && (s.userData.user_id ?? s.userData.pk)) || s.userId || '',
  );

  const players: RoyaleTournamentPlayerResult[] = useMemo(
    () =>
      (detail.results?.players || [])
        .slice()
        .sort((a, b) => (a.rank || 999) - (b.rank || 999)),
    [detail.results],
  );

  const names = useMemo(() => rosterNameMap(detail), [detail]);
  const nameFor = (uid: number) =>
    names[String(uid)] || `Player ${uid}`;

  const winner = players[0];
  const me = players.find(p => String(p.user_id) === String(myUserId));

  const [showMyPuzzles, setShowMyPuzzles] = useState(false);

  if (!detail.results || players.length === 0) {
    return (
      <Box style={styles.card}>
        <CCText style={styles.cardTitle}>Results</CCText>
        <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.GreyText}>
          This tournament has finished, but no results were recorded.
        </CCText>
      </Box>
    );
  }

  return (
    <>
      {/* Winner callout */}
      <Box style={[styles.card, styles.winnerCard]}>
        <CCText style={styles.crown}>👑</CCText>
        <CCText fontSize={FontSizes[16]} fontWeight="bold" color={BROWN}>
          {`${nameFor(winner.user_id)} wins`}
        </CCText>
        <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.GreyText}>
          {`${winner.points} pts · ${winner.solved_ids?.length ?? winner.score} solved`}
        </CCText>
      </Box>

      {/* Your race */}
      {me ? (
        <Box style={styles.card}>
          <CCText style={styles.cardTitle}>Your race</CCText>
          <View style={styles.myStats}>
            <Stat label="Rank" value={me.rank ? `#${me.rank}` : '—'} />
            <Divider />
            <Stat label="Points" value={String(me.points)} />
            <Divider />
            <Stat label="Solved" value={String(me.solved_ids?.length ?? me.score)} />
            <Divider />
            <Stat
              label="Accuracy"
              value={
                me.accuracy != null ? `${Math.round(me.accuracy)}%` : '—'
              }
            />
          </View>
          <Pressable
            onPress={() => setShowMyPuzzles(v => !v)}
            style={styles.expandRow}>
            <CCText fontSize={FontSizes[12]} color={TEAL_DARK} fontWeight="bold">
              {showMyPuzzles ? 'Hide puzzles' : 'View my puzzles'}
            </CCText>
            <MaterialCommunityIcons
              name={showMyPuzzles ? 'chevron-up' : 'chevron-down'}
              size={16}
              color={TEAL_DARK}
            />
          </Pressable>
          {showMyPuzzles ? (
            <View style={styles.puzzleBreakdown}>
              <View style={styles.puzzleLine}>
                <MaterialCommunityIcons name="check-circle" size={16} color={TEAL} />
                <CCText fontSize={FontSizes[13]} color={TEXT_DARK}>
                  {`${me.solved_ids?.length ?? 0} solved`}
                </CCText>
                {me.solved_ids && me.solved_ids.length ? (
                  <CCText
                    fontSize={FontSizes[11]}
                    color={CCColors.TextColor.GreyText}>
                    {`(#${me.solved_ids.join(', #')})`}
                  </CCText>
                ) : null}
              </View>
              <View style={styles.puzzleLine}>
                <MaterialCommunityIcons name="close-circle" size={16} color={RED} />
                <CCText fontSize={FontSizes[13]} color={TEXT_DARK}>
                  {`${me.failed_ids?.length ?? 0} missed`}
                </CCText>
                {me.failed_ids && me.failed_ids.length ? (
                  <CCText
                    fontSize={FontSizes[11]}
                    color={CCColors.TextColor.GreyText}>
                    {`(#${me.failed_ids.join(', #')})`}
                  </CCText>
                ) : null}
              </View>
            </View>
          ) : null}
        </Box>
      ) : null}

      {/* Final leaderboard */}
      <Box style={styles.card}>
        <CCText style={styles.cardTitle}>
          {`Final standings (${players.length})`}
        </CCText>
        <View style={styles.tableHead}>
          <CCText style={[styles.th, styles.thRank]}>#</CCText>
          <CCText style={[styles.th, styles.thName]}>Player</CCText>
          <CCText style={[styles.th, styles.thNum]}>Pts</CCText>
          <CCText style={[styles.th, styles.thNum]}>Solved</CCText>
          <CCText style={[styles.th, styles.thNum]}>Acc</CCText>
        </View>
        {players.map(p => {
          const isMe = String(p.user_id) === String(myUserId);
          return (
            <View
              key={p.user_id}
              style={[styles.tableRow, isMe && styles.tableRowMe]}>
              <View style={styles.tdRank}>
                <Medal rank={p.rank || 0} />
              </View>
              <CCText
                style={[styles.td, styles.tdName]}
                numberOfLines={1}>
                {nameFor(p.user_id)}
                {isMe ? ' (you)' : ''}
              </CCText>
              <CCText style={[styles.td, styles.tdNum]}>{p.points}</CCText>
              <CCText style={[styles.td, styles.tdNum]}>
                {p.solved_ids?.length ?? p.score}
              </CCText>
              <CCText style={[styles.td, styles.tdNum]}>
                {p.accuracy != null ? `${Math.round(p.accuracy)}%` : '—'}
              </CCText>
            </View>
          );
        })}
      </Box>
    </>
  );
}

function Stat({label, value}: {label: string; value: string}) {
  return (
    <View style={styles.stat}>
      <CCText fontSize={FontSizes[16]} fontWeight="bold" color={TEXT_DARK}>
        {value}
      </CCText>
      <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.GreyText}>
        {label}
      </CCText>
    </View>
  );
}

function Divider() {
  return <View style={styles.statDivider} />;
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: CCColors.White,
    borderRadius: 14,
    padding: vs(16),
    gap: vs(10),
    borderWidth: 1,
    borderColor: BORDER,
  },
  winnerCard: {alignItems: 'center', gap: 4, backgroundColor: '#FFFBF0'},
  crown: {fontSize: 30, lineHeight: 36},
  cardTitle: {
    fontSize: FontSizes[12],
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: BROWN,
  },
  myStats: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: TEAL_TINT_SOFT,
    borderRadius: 12,
    paddingVertical: vs(12),
    paddingHorizontal: vs(8),
  },
  stat: {flex: 1, alignItems: 'center', gap: 2},
  statDivider: {width: 1, height: 28, backgroundColor: BORDER},
  expandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: vs(4),
  },
  puzzleBreakdown: {gap: 8},
  puzzleLine: {flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap'},
  tableHead: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: vs(6),
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },
  th: {
    fontSize: FontSizes[11],
    color: CCColors.TextColor.GreyText,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  thRank: {width: 34},
  thName: {flex: 1},
  thNum: {width: 52, textAlign: 'right'},
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: vs(9),
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },
  tableRowMe: {backgroundColor: TEAL_TINT_SOFT, borderRadius: 8},
  td: {fontSize: FontSizes[13], color: TEXT_DARK},
  tdRank: {width: 34, alignItems: 'flex-start'},
  tdName: {flex: 1, fontWeight: '600'},
  tdNum: {width: 52, textAlign: 'right'},
  medal: {fontSize: 16, lineHeight: 20},
  rankNumWrap: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CCColors.Primary.lightBrown,
  },
});
